/**
 * Emision tipada hacia el SDK (protocolo 7.2.3 del plan).
 *
 * `@apipay/checkout-js` es la fuente de verdad del canal: el union se importa con
 * `import type`, de modo que el bundle del widget no arrastra ni un byte del SDK.
 *
 * Invariantes que hacen que el SDK acepte los mensajes (su cascada de 4
 * validaciones: origin, source, type guard y nonce):
 *  - `targetOrigin` es el origen embebedor YA validado por frameGuard, jamas '*';
 *  - todos los mensajes llevan el nonce de la sesion;
 *  - `apipay:resize` se acota a [0, 2000] px, la misma cota que aplica el SDK;
 *  - success/error/cancel son terminales: el primero gana y los siguientes se
 *    descartan, igual que hace el SDK del otro lado del canal.
 */
import type { WidgetToSdkMessage } from '@apipay/checkout-js';

/** Lo minimo que el bridge necesita del `window.parent`. */
export interface MessageTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface WidgetBridge {
  ready(): void;
  resize(height: number): void;
  success(paymentIntentId: string): void;
  error(code: string, message: string): void;
  cancel(): void;
}

export interface CreateBridgeArgs {
  nonce: string;
  targetOrigin: string;
  /** Solo para tests; en produccion se usa `window.parent`. */
  target?: MessageTarget | undefined;
}

const NONCE_PATTERN = /^[0-9a-f]{32}$/;

const MIN_HEIGHT_PX = 0;
const MAX_HEIGHT_PX = 2000;

function isAbsoluteOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin === value && (url.protocol === 'https:' || url.protocol === 'http:');
  } catch {
    return false;
  }
}

function defaultTarget(): MessageTarget {
  return {
    postMessage(message: unknown, targetOrigin: string): void {
      window.parent.postMessage(message, targetOrigin);
    },
  };
}

/**
 * Crea el bridge de la sesion. Falla ruidosamente si el nonce o el targetOrigin no
 * son utilizables: preferimos no emitir nada antes que emitir a un destino incierto.
 */
export function createBridge(args: CreateBridgeArgs): WidgetBridge {
  const { nonce, targetOrigin } = args;

  if (!NONCE_PATTERN.test(nonce)) {
    throw new Error('checkout-widget: nonce de sesion invalido; no se abre el canal postMessage.');
  }
  if (targetOrigin === '*' || !isAbsoluteOrigin(targetOrigin)) {
    throw new Error(
      `checkout-widget: targetOrigin invalido ("${targetOrigin}"). ` +
        'Debe ser el origen embebedor validado.',
    );
  }

  const target = args.target ?? defaultTarget();
  let settled = false;

  const post = (message: WidgetToSdkMessage): void => {
    target.postMessage(message, targetOrigin);
  };

  return {
    ready(): void {
      post({ type: 'apipay:ready', nonce });
    },

    resize(height: number): void {
      if (!Number.isFinite(height)) {
        return;
      }
      const clamped = Math.max(MIN_HEIGHT_PX, Math.min(Math.round(height), MAX_HEIGHT_PX));
      post({ type: 'apipay:resize', nonce, height: clamped });
    },

    success(paymentIntentId: string): void {
      if (settled || paymentIntentId.length === 0) {
        return;
      }
      settled = true;
      post({ type: 'apipay:success', nonce, paymentIntentId });
    },

    error(code: string, message: string): void {
      if (settled || code.length === 0 || message.length === 0) {
        return;
      }
      settled = true;
      post({ type: 'apipay:error', nonce, code, message });
    },

    cancel(): void {
      if (settled) {
        return;
      }
      settled = true;
      post({ type: 'apipay:cancel', nonce });
    },
  };
}
